import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    const addToCartBtn = document.querySelector('[ref="addToCartButton"]');
    const bisBtn = document.getElementById('BIS_trigger');

    if (addToCartBtn && bisBtn) {
      if (addToCartBtn.hasAttribute('disabled')) {
        bisBtn.style.setProperty('display', 'block', 'important');
      } else {
        bisBtn.style.setProperty('display', 'none', 'important');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true'
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);


    // Get variant values
    const variantOptionsInput = form.querySelector('input[name="variantOptions"]');
    if (!variantOptionsInput) {
      throw new Error('Variant options input is missing from the form.');
    }
    const formattedVariantOptions = JSON.parse(variantOptionsInput.value.replace(/'/g, '"'));

    function getSelectedVariantId(currentForm, variantOptionsData) {
      const normalize = (str) => str?.toString().toLowerCase().trim();
      // ✅ Scope the radio button search to the CURRENT form, not the whole document
      const selectedRadios = Array.from(
        currentForm.querySelectorAll('input[type="radio"]:checked')
      );

      // ✅ If no radio buttons exist in this form (like on a product card),
      // fall back to the default variant ID in the hidden input.
      if (selectedRadios.length === 0) {
        const defaultVariantInput = currentForm.querySelector('input[name="id"]');
        return defaultVariantInput ? defaultVariantInput.value : null;
      }

      // If radio buttons DO exist, find the matching variant from the selections
      const selectedValues = selectedRadios.map(r => normalize(r.value));
      const matched = variantOptionsData.find(v => {
        const variantOptionValues = [v.option1, v.option2, v.option3].map(normalize);
        return selectedValues.every(val => variantOptionValues.includes(val));
      });

      return matched?.id || null;
    }

    // Pass the current form and its options to the updated function
    const selectedVariantId = getSelectedVariantId(form, formattedVariantOptions);

    if (!selectedVariantId) {
      // The error is now more descriptive
      throw new Error('Could not determine a valid variant to add to the cart.');
    }

    formData.set('id', selectedVariantId);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const cartPopup = document.getElementById('CartPopup');

        fetch('/cart.js')
        .then((res) => {
         if (!res.ok) {
              return res.json().then(errorData => {
                throw new Error(errorData.message || errorData.description || 'Add to cart failed');
              });
            }
            return res.json();
          })
          .then((cart) => {
            const productSection = document.querySelectorAll(
              '#CartPopup .cart-popup-product, .cart-popup-empty',
            );
            productSection.forEach((el) => el.remove());

            const header = document.querySelector('#CartPopup .cart-popup-header');
            let insertHtml = '';

            if (cart.items.length > 0) {
              insertHtml = cart.items
                .map((item) => {
                  const options = item.options_with_values
                    .map(
                      (opt) => `
                          <div class="cart-popup-meta text-body-3-regular text-neutral-faded">
                            <span class="text-body-3-medium text-neutral">${opt.name}:</span> ${opt.value}
                          </div>`,
                    )
                    .join('');

                  return `
                        <div class="cart-popup-product">
                          <div class="cart-popup-produc-image">
                            <img src="${item.image}" alt="${
                    item.title
                  }" class="cart-popup-image" width="auto" height="auto">
                          </div>
                          <div class="cart-popup-details">
                            <div class="cart-popup-title text-body-2-medium text-neutral">
                              ${item.product_title}
                            </div>
                            <div class="cart-popup-price text-body-2-medium text-neutral-faded">
                              ${(item.final_line_price / 100).toLocaleString('en-US', {
                                style: 'currency',
                                currency: cart.currency || 'USD',
                              })}
                            </div>
                            
                            <!-- ✅ Quantity -->
                            <div class="cart-popup-quantity text-body-3-regular text-neutral-faded">
                              <span class="text-body-3-medium text-neutral">Qty: </span>${
                                item.quantity
                              }
                            </div>

                            ${options}
                          </div>
                        </div>`;
                })
                .join('');
              if (cartPopup) {
                cartPopup.classList.remove('hidden');
              }
            } else {
              const emptyText =
                document.getElementById('CartEmptyText')?.dataset.emptyText ||
                'Your cart is empty.';
              insertHtml = `
                    <div class="cart-popup-empty text-body-3-regular text-neutral-faded">
                      ${emptyText}
                    </div>`;

              if (cartPopup) {
                cartPopup.classList.addClass('hidden');
              }
            }

            header.insertAdjacentHTML('afterend', insertHtml);
            const viewCart = document.querySelector('#CartPopup .view-cart');
            viewCart.innerHTML = `View Cart (${cart.item_count})`;
            const cartBadge = document.querySelector('.cart-count-badge');
            if (cartBadge) {
              cartBadge.textContent = cart.item_count;
            }
            // ✅ Update the cart total price
            const cartPrice = document.querySelector('.cart-price');
            if (cartPrice) {
              cartPrice.textContent = (cart.total_price / 100).toLocaleString('en-US', {
                style: 'currency',
                currency: cart.currency || 'USD',
              });
            }
            // Show success message inside the form
            const successMessage = this.querySelector('.add-to-cart-success-message');
            if (successMessage) {
              successMessage.hidden = false;
              successMessage.classList.add('show');
              setTimeout(() => {
                successMessage.hidden = true;
                successMessage.classList.remove('show');
              }, 6000);
            }
          });
          if (window.location.pathname === '/cart') {
            window.scrollTo(0, 0);
            location.reload();
          }
          // updateCartSection();
          // updateCartSummary();
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            }),
          );
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );
        }
      })
      .catch((error) => {
      console.error('Add to cart error:', error);
      
      const { addToCartTextError } = this.refs;
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        addToCartTextError.textContent = error.message;
        
        setTimeout(() => {
          addToCartTextError.classList.add('hidden');
        }, 10000);
      }
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(`[ref="addToCartButton"]#${currentAddToCartButton?.id}`);

    if (!currentAddToCartButton) return;

    // Update the button state
    const bisBtn = document.getElementById('BIS_trigger');
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      if(bisBtn){
        bisBtn.style.setProperty('display', 'block', 'important');
      }
    } else {
      addToCartButtonContainer.refs.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      if(bisBtn){
        bisBtn.style.setProperty('display', 'none', 'important');
      }
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100',
        );
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

function updateCartSection() {
  const cartSection = document.querySelector('[data-section-id][data-cart-section]');
  if (!cartSection) return;

  const sectionId = cartSection.dataset.sectionId;

  fetch(`/?sections=${sectionId}`)
    .then((res) => res.json())
    .then((data) => {
      const newHtml = data[sectionId];
      if (!newHtml) return;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newHtml;

      const newWrapper = tempDiv.querySelector('[data-cart-section]');
      if (newWrapper) {
        cartSection.innerHTML = newWrapper.innerHTML;
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    })
    .catch((err) => console.error('Failed to update cart section:', err));
}
function updateCartSummary() {
  const summaryEl = document.querySelector('[data-cart-summary]');
  if (!summaryEl) return;

  const sectionId = summaryEl.dataset.sectionId;
  if (!sectionId) return;

  fetch(`/?sections=${sectionId}`)
    .then(res => res.json())
    .then(data => {
      const newHtml = data[sectionId];
      if (!newHtml) return;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newHtml;

      const newSummary = tempDiv.querySelector('[data-cart-summary]');
      if (newSummary) {
        summaryEl.innerHTML = newSummary.innerHTML;
      }
    })
    .catch(err => console.error('Failed to update cart summary:', err));
}